import { z } from 'zod';
import { keyDateIdSchema, type KeyDateId } from './ids';
import { MAX_HORIZON_MONTHS } from './limits';
import { moveStart, orderBounds } from './item-dates';
import type { Item } from './item';
import type { Project } from './project';
import { monthIndexSchema, type MonthIndex } from './time';

export const MAX_KEY_DATES = 50;

/**
 * A month the user names (e.g. "Building finished", "Keys handed over"). Dates elsewhere in the
 * project can be tied to it, so moving it moves them. The app attaches no meaning to the name.
 */
export const keyDateSchema = z.object({
  id: keyDateIdSchema,
  name: z.string().trim().min(1).max(80),
  month: monthIndexSchema,
});
export type KeyDate = z.infer<typeof keyDateSchema>;

/** A month tied to a key date: `keyDate.month + offset`. */
export const keyDateRefSchema = z.object({
  keyDate: keyDateIdSchema,
  offset: z.number().int().min(-MAX_HORIZON_MONTHS).max(MAX_HORIZON_MONTHS),
});
export type KeyDateRef = z.infer<typeof keyDateRefSchema>;

/**
 * `anchors` on an object ties its month fields to key dates, field by field (e.g. an item's
 * `{ start, end }`, an instalment's `{ month }`, a schedule period's `{ from }`). The tied months
 * are kept resolved, so the engine never sees key dates.
 */
export function anchorsSchema<const K extends string>(...fields: K[]) {
  const ref = keyDateRefSchema.optional();
  const shape = Object.fromEntries(fields.map((f) => [f, ref])) as Record<K, typeof ref>;
  return z.object(shape).optional();
}

type Path = (string | number)[];
type Json = Record<string, unknown>;

/**
 * Returns the month for a tied field (`month` is its current value), or null to untie it and
 * keep its current value.
 */
export type AnchorVisitor = (
  month: number | undefined,
  ref: KeyDateRef,
  path: readonly (string | number)[],
) => number | null;

const isObject = (v: unknown): v is Json => typeof v === 'object' && v !== null;

/** The date of an entry in a dated list (schedule periods, instalments, early repayments). */
function dateOf(entry: unknown): number | undefined {
  if (!isObject(entry)) return undefined;
  const date = entry.from ?? entry.month;
  return typeof date === 'number' ? date : undefined;
}

/** Keeps a dated list in order after a tied entry moved; other lists are left alone. */
function sortDated(list: unknown[]): unknown[] {
  if (list.length < 2 || !list.every((e) => dateOf(e) !== undefined)) return list;
  return list.sort((a, b) => (dateOf(a) ?? 0) - (dateOf(b) ?? 0));
}

/**
 * Applies `visit` to every tied month; unchanged parts of `value` keep their identity. Dated lists
 * (schedule periods, instalments, early repayments) in which a month moved are re-sorted.
 */
export function mapAnchors<T>(value: T, visit: AnchorVisitor): T {
  const path: Path = [];
  const walk = (node: unknown): unknown => {
    if (Array.isArray(node)) {
      const items = node as unknown[];
      let copy: unknown[] | null = null;
      for (let i = 0; i < items.length; i++) {
        const child = items[i];
        path.push(i);
        const next = walk(child);
        path.pop();
        if (next !== child) (copy ??= [...items])[i] = next;
      }
      return copy ? sortDated(copy) : node;
    }
    if (!isObject(node)) return node;
    let copy: Json | null = null;
    for (const [key, child] of Object.entries(node)) {
      if (key === 'anchors' || !isObject(child)) continue;
      path.push(key);
      const next = walk(child);
      path.pop();
      if (next !== child) (copy ??= { ...node })[key] = next;
    }
    const anchors = node.anchors;
    if (!isObject(anchors)) return copy ?? node;
    const untied = new Set<string>();
    for (const [field, ref] of Object.entries(anchors)) {
      if (ref === undefined) continue;
      const current = node[field];
      path.push(field);
      const month = visit(
        typeof current === 'number' ? current : undefined,
        ref as KeyDateRef,
        path,
      );
      path.pop();
      if (month === null) untied.add(field);
      else if (month !== current) (copy ??= { ...node })[field] = month;
    }
    if (untied.size === 0) return copy ?? node;
    return withAnchors(copy ?? node, without(anchors, untied));
  };
  return walk(value) as T;
}

/** `anchors` without `fields` (and without unset ones). */
function without(anchors: Json, fields: ReadonlySet<string>): Json {
  return Object.fromEntries(
    Object.entries(anchors).filter(([field, ref]) => ref !== undefined && !fields.has(field)),
  );
}

/** `target` with `anchors`, or without the property when there are none. */
function withAnchors(target: Json, anchors: Json): Json {
  const { anchors: _, ...rest } = target;
  return Object.keys(anchors).length > 0 ? { ...rest, anchors } : rest;
}

/**
 * Moves every tied month to its key date + offset. Run after every edit. An item whose start
 * moves takes its untied instalments, early repayments and end along (`moveStart`).
 */
export function resolveKeyDates(project: Project): Project {
  if (project.keyDates.length === 0) return project;
  const months = new Map(project.keyDates.map((k) => [k.id, k.month]));
  const resolved = mapAnchors(project, (month, ref) => {
    const at = months.get(ref.keyDate);
    return at === undefined ? (month ?? null) : at + ref.offset;
  });
  if (resolved === project) return project;
  let items: Record<string, Item> | null = null;
  for (const [key, item] of Object.entries(resolved.items)) {
    const before = project.items[key];
    if (!before || before.start === item.start) continue;
    (items ??= { ...resolved.items })[key] = moveStart(
      { ...item, start: before.start },
      item.start,
    );
  }
  return items ? { ...resolved, items } : resolved;
}

/** Calls `visit` with every list of dated entries (schedule periods, instalments, …) and its path. */
function forEachDatedList(
  node: unknown,
  visit: (list: readonly Json[], path: readonly (string | number)[]) => void,
  path: (string | number)[] = [],
): void {
  if (Array.isArray(node)) {
    const list = node as unknown[];
    if (list.length > 0 && list.every((e) => dateOf(e) !== undefined)) visit(list as Json[], path);
    else list.forEach((e, i) => forEachDatedList(e, visit, [...path, i]));
  } else if (isObject(node)) {
    for (const [key, child] of Object.entries(node)) {
      if (key !== 'anchors') forEachDatedList(child, visit, [...path, key]);
    }
  }
}

/** The key date an entry of a dated list is tied to. */
function entryRef(entry: Json): KeyDateRef | undefined {
  const anchors = entry.anchors;
  if (!isObject(anchors)) return undefined;
  return (anchors.from ?? anchors.month) as KeyDateRef | undefined;
}

/**
 * The months each key date can move to while its tied dates stay valid: none below month 0,
 * schedule periods (never the first) in months 1 to the horizon, periods and instalments in order
 * (what moves with the key date doesn't reach what stays), and an item's dates in order with its
 * start (a sale, tied or not, after it). Always includes the key date's current month. One pass
 * over the project for all key dates.
 */
export function keyDateRanges(project: Project): Map<KeyDateId, [number, number]> {
  const last = project.assumptions.horizonMonths - 1;
  const months = new Map(project.keyDates.map((k) => [k.id, k.month]));
  const bounds = new Map<KeyDateId, [number, number]>(
    project.keyDates.map((k) => [k.id, [0, last]]),
  );
  /** Key date `id`'s month must be ≥ `min` and ≤ `max`. */
  const bound = (id: KeyDateId, min: number, max = Infinity): void => {
    const b = bounds.get(id);
    if (!b) return;
    b[0] = Math.max(b[0], min);
    b[1] = Math.min(b[1], max);
  };
  mapAnchors(project, (month, ref, path) => {
    bound(ref.keyDate, -ref.offset);
    if (path.at(-1) === 'from') bound(ref.keyDate, 1 - ref.offset, last - ref.offset);
    return month ?? null;
  });
  forEachDatedList(project, (unsorted, path) => {
    // Early repayments may share a month and come in any order; periods and instalments may not.
    if (path.at(-1) === 'earlyRepayments') return;
    const list = [...unsorted].sort((a, b) => (dateOf(a) ?? 0) - (dateOf(b) ?? 0));
    // An item's untied instalments move with its start.
    const owner =
      path[0] === 'items' && path.length === 3 ? project.items[path[1] ?? ''] : undefined;
    const startKey = owner?.anchors?.start?.keyDate;
    const refs = list.map(entryRef);
    const ids = new Set(refs.flatMap((r) => (r ? [r.keyDate] : [])));
    if (startKey !== undefined) ids.add(startKey);
    for (const id of ids) {
      const current = months.get(id);
      if (current === undefined) continue;
      const moves = refs.map((r) => (r === undefined ? startKey === id : r.keyDate === id));
      const [min, max] = orderBounds(
        list.map((e) => dateOf(e) ?? 0),
        moves,
        current,
      );
      bound(id, min, max);
    }
  });
  for (const item of Object.values(project.items)) {
    const start = item.anchors?.start;
    /** A date that must stay within start + `gap` … start + `upTo`. */
    const within = (month: number, ref: KeyDateRef | undefined, gap: number, upTo = Infinity) => {
      if (ref === undefined || ref.keyDate === start?.keyDate) return;
      // The date moves with its key date while the start stays …
      bound(ref.keyDate, item.start + gap - ref.offset, item.start + upTo - ref.offset);
      // … and the start moves with its own while the date stays.
      if (start) bound(start.keyDate, month - upTo - start.offset, month - gap - start.offset);
    };
    switch (item.type) {
      case 'flow':
      case 'formula':
        if (item.end !== undefined) within(item.end, item.anchors?.end, 0);
        break;
      case 'asset':
        for (const p of item.payments ?? []) within(p.month, p.anchors?.month, 0);
        if (item.liquidateAt === undefined) break;
        within(item.liquidateAt, item.anchors?.liquidateAt, 1);
        // An untied sale stays put too: the start can't reach it.
        if (start && item.anchors?.liquidateAt === undefined) {
          bound(start.keyDate, -Infinity, item.liquidateAt - 1 - start.offset);
        }
        break;
      case 'loan':
        for (const r of item.earlyRepayments) within(r.month, r.anchors?.month, 1, item.termMonths);
        break;
    }
  }
  for (const [id, b] of bounds) {
    b[1] = Math.max(b[0], b[1]);
    // A key date left past a shortened horizon (or otherwise out of range) keeps its month.
    const current = months.get(id) ?? b[0];
    b[0] = Math.min(b[0], current);
    b[1] = Math.max(b[1], current);
  }
  return bounds;
}

/** The months key date `id` can move to (see `keyDateRanges`). */
export function keyDateRange(project: Project, id: KeyDateId): [number, number] {
  return keyDateRanges(project).get(id) ?? [0, project.assumptions.horizonMonths - 1];
}

/** Unties every month tied to `id`; they keep their current values. */
export function untieKeyDate(project: Project, id: KeyDateId): Project {
  return mapAnchors(project, (month, ref) => (ref.keyDate === id ? null : (month ?? null)));
}

/** How many months are tied to each key date. */
export function keyDateUsage(project: Project): Map<KeyDateId, number> {
  const counts = new Map<KeyDateId, number>();
  mapAnchors(project, (month, ref) => {
    counts.set(ref.keyDate, (counts.get(ref.keyDate) ?? 0) + 1);
    return month ?? null;
  });
  return counts;
}

/**
 * Sets a month field and its tie: `ref` ties it to a key date, undefined leaves it free.
 * `month` should already be the key date + offset when tied.
 */
export function withMonth<T extends object>(
  target: T,
  field: string & keyof T,
  month: MonthIndex | undefined,
  ref?: KeyDateRef,
): T {
  const current = (target as Json).anchors;
  const others = without(isObject(current) ? current : {}, new Set([field]));
  const anchors = ref ? { ...others, [field]: ref } : others;
  const set: Json = Object.fromEntries(Object.entries(target).filter(([key]) => key !== field));
  if (month !== undefined) set[field] = month;
  return withAnchors(set, anchors) as T;
}
